import { useEffect, useRef } from "react"
import { v4 } from "uuid"
import { submitToolResult } from "@utils/rsrhSubmissions"

interface ToolSubmissionData {
  fields: Record<string, unknown>
  items?: Record<string, unknown>[]
}

// Shared save-on-complete behaviour for blocks/rsrh/*.tsx tools: generates a per-session id,
// fires a single best-effort submission to Airtable (via /wp-json/rsrh/submit) once `isComplete`
// becomes true, and stays silent (console-only) on failure so it never blocks the tool's own UI.
//
// Call `resetSubmission()` from the tool's own restart handler so a re-run gets a fresh session.
export function useToolSubmission<T extends ToolSubmissionData>(
  tool: string,
  isComplete: boolean,
  buildPayload: () => T,
) {
  const sessionIdRef = useRef(v4())
  const hasSubmittedRef = useRef(false)

  useEffect(() => {
    if (!isComplete || hasSubmittedRef.current) return
    hasSubmittedRef.current = true

    const { fields, items } = buildPayload()
    submitToolResult({
      tool,
      sessionId: sessionIdRef.current,
      sourceUrl: window.location.href,
      hp: "",
      fields,
      items,
    }).catch((err) => {
      console.error(`[useToolSubmission:${tool}] failed to save results to Airtable`, err)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete])

  const resetSubmission = () => {
    hasSubmittedRef.current = false
    sessionIdRef.current = v4()
  }

  return { resetSubmission, sessionId: sessionIdRef.current }
}
